import { reportError } from './reportError';

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

type ParseApiResponseOptions = {
  allowNoContent?: boolean;
};

export function getApiErrorMessage(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (!isRecord(payload)) {
    return fallback;
  }

  if (typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  const error = payload.error;
  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  if (isRecord(error) && typeof error.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

/**
 * Lê o `error.code` do envelope de erro do core-api. É por ele, e não pelo texto,
 * que o consumidor separa causas distintas sob um mesmo status (dois 409 diferentes).
 */
export function getApiErrorCode(payload: unknown): string | null {
  if (!isRecord(payload) || !isRecord(payload.error)) return null;
  return typeof payload.error.code === 'string' ? payload.error.code : null;
}

/**
 * Le o corpo de uma resposta nao-ok e lanca `Error` com a mensagem do servidor.
 * Substitui as copias locais de `handleApiError` que cada feature mantinha.
 */
export async function throwApiError(response: Response, fallback: string): Promise<never> {
  const body: unknown = await response.json().catch(() => null);
  throw new Error(getApiErrorMessage(body, fallback));
}

export async function parseApiResponse<T>(response: Response): Promise<T>;
export async function parseApiResponse<T>(
  response: Response,
  options: { allowNoContent: true },
): Promise<T | undefined>;
export async function parseApiResponse<T>(
  response: Response,
  options: ParseApiResponseOptions = {},
): Promise<T | undefined> {
  const fallbackError = `HTTP ${response.status}`;

  if (!response.ok) {
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      const errorText = await response.text();
      throw new Error(errorText || fallbackError);
    }

    throw new Error(getApiErrorMessage(payload, fallbackError));
  }

  if (response.status === 204) {
    if (options.allowNoContent) {
      return undefined;
    }

    throw new Error('Resposta sem conteudo inesperada da API');
  }

  const payload = (await response.json()) as unknown;

  if (!isRecord(payload)) {
    return payload as T;
  }

  if (payload.success === false) {
    throw new Error(getApiErrorMessage(payload, 'Erro na resposta da API'));
  }

  if (payload.success === true) {
    return ('data' in payload ? payload.data : undefined) as T;
  }

  if ('data' in payload) {
    return payload.data as T;
  }

  return payload as T;
}

type UnwrapApiDataOptions = {
  fallback: string;
  source: string;
};

/**
 * Variante estrita de `parseApiResponse` para superfícies públicas: exige o
 * envelope `{ data }`, reporta envelope vazio via telemetria e, em 5xx, propaga
 * apenas o `fallback` (a mensagem interna do servidor não vai para a tela).
 */
export async function unwrapApiData<T>(
  response: Response,
  { fallback, source }: UnwrapApiDataOptions,
): Promise<T> {
  if (!response.ok) {
    if (response.status >= 500) {
      throw new Error(fallback);
    }
    const payload: unknown = await response.json().catch(() => null);
    throw new Error(getApiErrorMessage(payload, fallback));
  }

  const payload = (await response.json().catch(() => null)) as { data?: T } | null;
  if (!payload || payload.data == null) {
    const erro = new Error(fallback);
    reportError(erro, { source, status: response.status });
    throw erro;
  }

  return payload.data;
}
